import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AnimatedFeedbackPopup from "../AnimatedFeedbackPopup";
import {
  SUPERMARKET_DESKTOP_PLACEMENTS,
  SUPERMARKET_MOBILE_PLACEMENTS,
} from "../../../data/supermarketPlacements";

function useNarrowLayout() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

function inside(clientX, clientY, rect) {
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
}

export default function SortingStage({ data, onDone, onSessionPointsChange }) {
  const isMobileLayout = useNarrowLayout();
  const sceneRef = useRef(null);
  const healthyBasketRef = useRef(null);
  const unhealthyBasketRef = useRef(null);
  const dragRef = useRef(null);
  const dragGhostRef = useRef(null);
  const windowListenersRef = useRef(null);
  const completionTriggeredRef = useRef(false);

  const [dropped, setDropped] = useState({});
  const [correctIds, setCorrectIds] = useState([]);
  const [popup, setPopup] = useState({ open: false, type: "ok" });
  /** Touch / pointer drag (mobile only) */
  const [drag, setDrag] = useState(null);

  const placements = isMobileLayout ? SUPERMARKET_MOBILE_PLACEMENTS : SUPERMARKET_DESKTOP_PLACEMENTS;

  const rackBackground = isMobileLayout
    ? data.mobileRackImage || data.rackImage || data.background
    : data.rackImage || data.background;

  const droppedCount = useMemo(() => Object.keys(dropped).length, [dropped]);
  const pointsPerItem = data.pointsPerItem ?? 5;
  const score = correctIds.length * pointsPerItem;
  const allDone = droppedCount === data.items.length;

  useEffect(() => {
    onSessionPointsChange?.(score);
  }, [onSessionPointsChange, score]);

  useEffect(() => {
    if (!allDone || completionTriggeredRef.current) return;
    completionTriggeredRef.current = true;
    onDone(score);
  }, [allDone, onDone, score]);

  const showPopup = useCallback((type) => {
    setPopup({ open: true, type });
    setTimeout(() => setPopup({ open: false, type }), 1000);
  }, []);

  const finalizeBucketDrop = useCallback(
    (id, bucket) => {
      if (!id || dropped[id]) return;
      const item = data.items.find((it) => it.id === id);
      if (!item) return;

      const isCorrect = item.answer === bucket;
      setDropped((prev) => ({ ...prev, [id]: bucket }));
      if (isCorrect) {
        setCorrectIds((prev) => [...prev, id]);
      }
      showPopup(isCorrect ? "ok" : "wrong");
    },
    [data.items, dropped, showPopup]
  );

  const handleDrop = (bucket, e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    finalizeBucketDrop(id, bucket);
  };

  useEffect(
    () => () => {
      const L = windowListenersRef.current;
      if (L) {
        window.removeEventListener("pointermove", L.onMove);
        window.removeEventListener("pointerup", L.onUp);
        window.removeEventListener("pointercancel", L.onUp);
        windowListenersRef.current = null;
      }
      if (dragGhostRef.current && dragGhostRef.current.parentNode) {
        dragGhostRef.current.parentNode.removeChild(dragGhostRef.current);
      }
      dragGhostRef.current = null;
    },
    []
  );

  const handleDesktopDragStart = useCallback((e, item) => {
    e.dataTransfer.setData("text/plain", item.id);
    e.dataTransfer.effectAllowed = "move";
    const sourceEl = e.currentTarget;
    const rect = sourceEl.getBoundingClientRect();

    const ghost = sourceEl.cloneNode(true);
    ghost.style.position = "fixed";
    ghost.style.top = "-10000px";
    ghost.style.left = "-10000px";
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.objectFit = "contain";
    ghost.style.pointerEvents = "none";
    ghost.style.opacity = "1";
    ghost.style.transform = "none";
    ghost.style.zIndex = "999999";
    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;

    // Keep drag preview size equal to rendered shelf item size.
    e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
  }, []);

  const handleDesktopDragEnd = useCallback(() => {
    if (dragGhostRef.current && dragGhostRef.current.parentNode) {
      dragGhostRef.current.parentNode.removeChild(dragGhostRef.current);
    }
    dragGhostRef.current = null;
  }, []);
 const handlePointerDownMobile = (e, placement) => {
    if (!isMobileLayout) return;
    if (dropped[placement.id]) return;
    if (e.button !== 0) return;
    e.preventDefault();

    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const dragInfo = {
      id: placement.id,
      placement,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      left: rect.left,
      top: rect.top,
      pointerId: e.pointerId,
    };

    try {
      el.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }

    dragRef.current = dragInfo;
    setDrag(dragInfo);

    const onMove = (ev) => {
      if (dragInfo.pointerId != null && ev.pointerId !== dragInfo.pointerId) return;
      const d = dragRef.current;
      if (!d) return;
      const next = {
        ...d,
        left: ev.clientX - d.offsetX,
        top: ev.clientY - d.offsetY,
      };
      dragRef.current = next;
      setDrag(next);
    };

    const onUp = (ev) => {
      if (dragInfo.pointerId != null && ev.pointerId !== dragInfo.pointerId) return;

      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      windowListenersRef.current = null;

      try {
        el.releasePointerCapture(dragInfo.pointerId);
      } catch (_) {
        /* ignore */
      }

      const x = ev.clientX;
      const y = ev.clientY;
      const healthyEl = healthyBasketRef.current;
      const unhealthyEl = unhealthyBasketRef.current;

      let bucket = null;
      if (healthyEl) {
        const hr = healthyEl.getBoundingClientRect();
        if (inside(x, y, hr)) bucket = "healthy";
      }
      if (!bucket && unhealthyEl) {
        const ur = unhealthyEl.getBoundingClientRect();
        if (inside(x, y, ur)) bucket = "unhealthy";
      }

      dragRef.current = null;
      setDrag(null);

      if (bucket) {
        finalizeBucketDrop(placement.id, bucket);
      }
    };

    windowListenersRef.current = { onMove, onUp };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const dragOverlay =
    isMobileLayout &&
    drag &&
    createPortal(
      <img
        src={drag.placement.src}
        alt=""
        className="market-draggable market-draggable--floating"
        draggable={false}
        style={{
          position: "fixed",
          left: drag.left,
          top: drag.top,
          width: drag.width,
          height: drag.height,
          zIndex: 10050,
          pointerEvents: "none",
          objectFit: "contain",
        }}
      />,
      document.body
    );

  return (
    <div className={`supermarket-stage${isMobileLayout ? " supermarket-stage--mobile" : ""}`}>
      <h2>THERE HAS BEEN A RAID IN THE MARKET BY WEAKER&apos;S ALLIES</h2>
      <h2>ALL THE FOOD LABELS HAVE BEEN MIXED UP!</h2>
      <h2>CITIZENS NOW CANNOT DIFFERENTIATE BETWEEN HEALTHY AND JUNK FOOD</h2>
      <h3>Can you help? Drag and drop them into the baskets.</h3>

      <div
        ref={sceneRef}
        className={`market-scene${isMobileLayout ? " market-scene--mobile" : ""}`}
        style={{ backgroundImage: `url(${rackBackground})` }}
      >
        {placements.map((item) => {
          if (dropped[item.id]) return null;

          const isDraggingThis = drag && drag.id === item.id;
          const baseStyle = {
            left: item.left,
            top: item.top,
            width: item.width,
            height: item.height,
            position: "absolute",
          };

          if (isMobileLayout) {
            return (
              <img
                key={item.id}
                src={item.src}
                alt={`market-${item.id}`}
                draggable={false}
                className={`market-draggable${isDraggingThis ? " market-draggable--placeholder" : ""}`}
                onPointerDown={(e) => handlePointerDownMobile(e, item)}
                style={{
                  ...baseStyle,
                  touchAction: "none",
                }}
              />
            );
          }

          return (
            <img
              key={item.id}
              src={item.src}
              alt={`market-${item.id}`}
              draggable
              onDragStart={(e) => handleDesktopDragStart(e, item)}
              onDragEnd={handleDesktopDragEnd}
              className="market-draggable"
              style={baseStyle}
            />
          );
        })}
      </div>

      <div className="market-baskets-row">
        <div
          ref={healthyBasketRef}
          className="market-basket basket-healthy"
          onDragOver={!isMobileLayout ? (e) => e.preventDefault() : undefined}
          onDrop={!isMobileLayout ? (e) => handleDrop("healthy", e) : undefined}
        >
          <img src={data.healthyBasket} alt="Healthy Basket" />
        </div>
        <div
          ref={unhealthyBasketRef}
          className="market-basket basket-unhealthy"
          onDragOver={!isMobileLayout ? (e) => e.preventDefault() : undefined}
          onDrop={!isMobileLayout ? (e) => handleDrop("unhealthy", e) : undefined}
        >
          <img src={data.unhealthyBasket} alt="Unhealthy Basket" />
        </div>
      </div>

      {dragOverlay}
      <AnimatedFeedbackPopup
        open={popup.open}
        type={popup.type === "ok" ? "success" : "error"}
        title={popup.type === "ok" ? "Awesome!" : "Oops! Wrong"}
      />
    </div>
  );
}
